import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Home, Building2, MapPin, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  residential: Home,
  commercial: Building2,
  land: MapPin
};

const CATEGORY_COLORS = {
  residential: "bg-blue-100 text-blue-700 border-blue-200",
  commercial: "bg-purple-100 text-purple-700 border-purple-200",
  land: "bg-green-100 text-green-700 border-green-200"
};

export default function PropertyTemplates({ onSelectTemplate, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/property-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    toast.success(`Template "${template.name}" applied!`);
  };

  const filteredTemplates = selectedCategory === "all"
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  const categories = ["all", "residential", "commercial", "land"];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-[#0f2744]" />
          <span className="text-slate-600">Loading templates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#0f2744] text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#c9a227] p-2 rounded-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Property Templates</h2>
                <p className="text-white/70 text-sm">Select a template to pre-fill the form</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="close-templates-modal"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((category) => {
              const Icon = CATEGORY_ICONS[category] || Sparkles;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                    selectedCategory === category
                      ? "bg-[#0f2744] text-white"
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                  data-testid={`filter-${category}`}
                >
                  {category !== "all" && <Icon className="h-4 w-4" />}
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const Icon = CATEGORY_ICONS[template.category] || Home;
              const colorClass = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.residential;
              
              return (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-[#c9a227] transition-all group"
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${colorClass}`}>
                      {template.category}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-[#0f2744] mb-2 group-hover:text-[#c9a227] transition-colors font-['Outfit']">
                    {template.name}
                  </h3>
                  
                  <div className="text-sm text-slate-500 space-y-1">
                    {template.bedrooms && (
                      <p>{template.bedrooms} BHK • {template.bathrooms} Bath</p>
                    )}
                    {template.area && (
                      <p>{template.area} sq.ft</p>
                    )}
                    {template.amenities?.length > 0 && (
                      <p className="text-xs text-slate-400 truncate">
                        {template.amenities.slice(0, 3).join(", ")}
                        {template.amenities.length > 3 && ` +${template.amenities.length - 3} more`}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-[#c9a227] font-medium group-hover:underline">
                      Use this template →
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-slate-500">No templates found for this category</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
          <p className="text-sm text-slate-500">
            {templates.length} templates available
          </p>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-300"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
